/**
 * Skeleton: a placeholder the shape of the content that is loading.
 *
 * The third of the three states every async surface needs, alongside
 * `EmptyState` and `ErrorState`. The previous build had none of them, so a screen
 * that had not loaded yet rendered confident zero counts as though they were facts.
 *
 * The shape itself is hidden from assistive technology. The caller announces the
 * loading state in a live region, because a screen reader user needs the word
 * "loading", not a description of a grey rectangle.
 *
 * Audit references prevented here: calendar.jsx #12, nearby.jsx #17, food.jsx #20.
 *
 * @param {object} props
 * @param {number|string} [props.height]  CSS height. A number is treated as px.
 * @param {number|string} [props.width]   CSS width. A number is treated as px.
 * @param {number} [props.lines]          Render this many stacked bars.
 * @param {string} [props.label]          Announced loading text. Pass null to
 *   suppress it when an ancestor already announces the same thing.
 */
export function Skeleton({ height = 16, width = '100%', lines = 1, label = 'Loading' }) {
  const bars = Array.from({ length: Math.max(1, lines) }, (_, index) => index);

  return (
    <>
      {label ? (
        <span className="visually-hidden" role="status">
          {label}
        </span>
      ) : null}
      <span className="skeleton-stack" aria-hidden="true">
        {bars.map((index) => (
          <span
            key={index}
            className="skeleton"
            style={{
              height: typeof height === 'number' ? `${height}px` : height,
              // The last bar of a multi-line block is short, the way a final line
              // of text is, so the placeholder reads as text rather than a block.
              width:
                lines > 1 && index === bars.length - 1
                  ? '62%'
                  : typeof width === 'number'
                    ? `${width}px`
                    : width,
            }}
          />
        ))}
      </span>
    </>
  );
}
